import { MileageUnit } from "./types";

export const WEEKDAYS = [
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
  "sun",
] as const;

export type Weekday = (typeof WEEKDAYS)[number];

export const MAX_ROWS_DOWNLOAD_LIMIT = 1000000;
export const MAX_TOP_CONTRIBUTORS_ROWS_DOWNLOAD_LIMIT = 2000;
export const MAX_SIGNAL_EVENT_OCCURRENCES_DOWNLOAD_LIMIT = 20000;
export const ISSUES_ROUTE = "issues";
export const DEFAULT_MILEAGE_UNIT: MileageUnit = "mi";
export const NONE_EXPOSURE = "None";
export const DEFAULT_EXPOSURE = "None";
export const DEFAULT_BY_VEHICLE_AGE = "vehicleManufacturedAt";
export const DEFAULT_EXPOSURE_BUCKET = 36;

export const TABLE_STATE_LOCAL_STORAGE_PREFIX = "filterSortState";
// This bumps ALL our localStorage & URL keys, so we can invalidate all of them at once.
// USE SPARINGLY!
export const GLOBAL_PAGE_KEY_PREFIX = "v1";
export const OPENING_BRACKETS = ["(", "{", "["];
export const CLOSING_BRACKETS = [")", "}", "]"];
// Display date/time formats — passed to formatDate (shared/locale/format) for
// user-facing, locale-aware rendering via Intl.DateTimeFormat. These strings are
// identifiers, not date-fns patterns: they are stored as an attribute's dateFormat
// and the API uses the same values as defaults, so never pass them to date-fns
// format() or change their values.
export const LONG_DATE_FORMAT = "LLLL d, yyyy";
export const SHORT_DATE_FORMAT = "MM/dd/yyyy";
export const VERY_SHORT_DATE_FORMAT = "M/d/yy";
export const DATE_WITH_TIME_FORMAT = "MM/dd/yyyy H:mm:ss a";
export const DATE_WITH_TIME_NO_SECONDS = "MM/dd/yyyy H:mm a";
export const DATE_WITH_TIME_AND_ZONE_FORMAT = "MM/dd/yyyy H:mm:ss a (zzz)";
export const MONTH_YEAR_SHORT = "MMM yy";
export const MONTH_YEAR = "MMM yyyy";
export const MONTH_YEAR_FULL = "MMMM yyyy";
export const MONTH_YEAR_DAY = "MMM, d yyyy";
export const MONTH_DAY = "MMM d";
export const MONTH_DAY_NUMERIC = "M/d";
export const MONTH_ONLY = "MMM";
export const YEAR_ONLY = "yyyy";
export const HOUR_ONLY = "h a";
export const TIME_ONLY = "h:mm a";
export const TIME_WITH_SECONDS = "HH:mm:ss";

// Every display format must have a matching DATE_FORMAT_OPTIONS entry in
// shared/locale/format — the Record there is keyed by DisplayDateFormat, so
// adding one here without the other fails tsc.
export const DISPLAY_DATE_FORMATS = [
  LONG_DATE_FORMAT,
  SHORT_DATE_FORMAT,
  VERY_SHORT_DATE_FORMAT,
  DATE_WITH_TIME_FORMAT,
  DATE_WITH_TIME_NO_SECONDS,
  DATE_WITH_TIME_AND_ZONE_FORMAT,
  MONTH_YEAR_SHORT,
  MONTH_YEAR,
  MONTH_YEAR_FULL,
  MONTH_YEAR_DAY,
  MONTH_DAY,
  MONTH_DAY_NUMERIC,
  MONTH_ONLY,
  YEAR_ONLY,
  HOUR_ONLY,
  TIME_ONLY,
  TIME_WITH_SECONDS,
] as const;

export type DisplayDateFormat = (typeof DISPLAY_DATE_FORMATS)[number];

// API date/time formats — exact, locale-independent strings for API query params
// and payloads (built with date-fns format()). ISO_FORMAT is special-cased in
// formatDate to date.toISOString(). Never route these through display formatting.
export const API_DATE_FORMAT = "yyyy-MM-dd";
export const API_DATE_FORMAT_W_TIME = "yyyy-MM-dd'T'HH:mm:ss";
export const ISO_FORMAT = "ISO_FORMAT";

export const TIME_AGO_THRESHOLD_HOURS = 12;
export const SENSORS_LIMIT = 1000;

export const TAB_QUERY_PARAM = "tab";

// min/max filter sentinels: what's actually sent to the API, and recognized by
// formatDate (see SPECIAL_DATE_VALUES in shared/locale/format) so a min/max date
// value isn't parsed as a date. Display labels live in the `filters` namespace
// under `min_max.<type>.<id>`.
export const MAX_FILTER_ID = "max";
export const MIN_FILTER_ID = "min";

export const CLOUD_DOMAIN = "cloud.viaduct.ai";
export const LOCAL_TENANTS = ["viaduct", "local"];
export const DEFAULT_LOCAL_API_BASE_URL = `api.viaduct.${CLOUD_DOMAIN}`;
export const DEFAULT_LOCAL_AI_BASE_URL = `ai.viaduct.${CLOUD_DOMAIN}`;

export const SERVICE_LOCALPORT_MAP: Record<string, number> = {
  api: parseInt(import.meta.env.VITE_API_PORT || "3001", 10),
  ai: parseInt(import.meta.env.VITE_AI_PORT || "3002", 10),
};

export const VALUES_SEPARATOR = "___";
export const EMPTY_CHART_LABEL = "(unknown)";
